import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import ModelTrainingIcon from '@mui/icons-material/ModelTraining'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Tooltip,
    Typography
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { ExportLimitReference, UserRow } from '../../um_scopes'
import UserManagerFormScope from '../um_form_scope'

const LOG = Logger.get('UserManagerFormView')

const useStyles = makeStyles()((theme) => ({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        minHeight: 400,
        minWidth: 800,
        rowGap: 10
    },
    dialogTitle: {
        backgroundColor: '#030303',
        color: '#6cea48'
    },
    flexGround: {
        flexGrow: 1
    },
    baseCard: {
        padding: 16,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 8
    },
    alertBox: {
        marginTop: 20,
        background: theme.palette.warning.light, //'#fdeaea',
        borderRadius: 10,
        border: `1px solid ${theme.palette.warning.main}`,
        padding: 18,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start'
    },

    alertTitle: {
        fontWeight: 700,
        marginBottom: 8,
        fontSize: '1rem',
        display: 'flex',
        gap: '2px'
    }
}))

type UserManagerFormViewProps = {
    className?: string
    scope: UserManagerFormScope
}
class UserManagerFormViewClass implements FCClassContext<UserManagerFormViewProps> {
    // :: Fields
    scope!: UserManagerFormScope
    sytles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitOnChangePassword = () => {
        this.scope.onChangePassword().catch(LOG.caught)
    }

    readonly emitGenerateRandomPassword = () => {
        this.scope.onGenerateRandomPassword().catch(LOG.caught)
    }

    readonly handleClickShowPassword = () => {
        this.scope.showPassword = !this.scope.showPassword
    }

    readonly emitFormDataChange = (dadosAlterados: Partial<UserRow>) => {
        this.scope.onFormDataChange(dadosAlterados).catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: UserManagerFormViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: UserManagerFormViewProps) {
        this.scope = bindUpdate(React, scope)
        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        const PermSwitch = (prop: keyof UserRow, label: string) => (
            <FormControlLabel
                control={
                    <Switch
                        checked={Boolean(scope.formData[prop])}
                        onChange={(evt) =>
                            this.emitFormDataChange({
                                [prop]: evt.target.checked
                            } as Partial<UserRow>)
                        }
                    />
                }
                label={label}
            />
        )

        return (
            <div className={clsx(classes.view, className)}>
                <Card elevation={3} sx={{ overflowY: 'auto' }}>
                    <CardHeader
                        className={classes.dialogTitle}
                        title={`${scope.userEditing?.id ? 'Editar' : 'Novo'} Usuário`}
                    />

                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Card elevation={0} className={classes.baseCard}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2
                                }}
                            >
                                <TextField
                                    fullWidth
                                    label="Usuário"
                                    variant="standard"
                                    disabled={scope.protectedGestor}
                                    value={scope.formData.name || ''}
                                    onChange={(e) => this.emitFormDataChange({ name: e.target.value })}
                                />

                                {!scope.userEditing?.id ? (
                                    <TextField
                                        fullWidth
                                        label="Senha"
                                        type={this.scope.showPassword ? 'text' : 'password'}
                                        variant="standard"
                                        value={scope.formData.password || ''}
                                        onChange={(e) => this.emitFormDataChange({ password: e.target.value })}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Gerar senha aleatória">
                                                            <IconButton
                                                                aria-label="toggle password visibility"
                                                                onClick={this.emitGenerateRandomPassword}
                                                                edge="end"
                                                            >
                                                                <ModelTrainingIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={
                                                                this.scope.showPassword
                                                                    ? 'Esconder senha'
                                                                    : 'Mostrar senha'
                                                            }
                                                        >
                                                            <IconButton
                                                                aria-label="toggle password visibility"
                                                                onClick={this.handleClickShowPassword}
                                                                edge="end"
                                                            >
                                                                {scope.showPassword ? (
                                                                    <VisibilityOff />
                                                                ) : (
                                                                    <Visibility />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }
                                        }}
                                    />
                                ) : (
                                    <Button
                                        variant="outlined"
                                        disabled={scope.protectedGestor}
                                        onClick={this.emitOnChangePassword}
                                    >
                                        Alterar senha
                                    </Button>
                                )}
                            </Box>

                            <TextField
                                fullWidth
                                label="Nome completo"
                                variant="standard"
                                sx={{ mt: 2, mb: 2 }}
                                value={scope.formData.full_name || ''}
                                onChange={(e) => this.emitFormDataChange({ full_name: e.target.value })}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2
                                }}
                            >
                                <TextField
                                    fullWidth
                                    label="Telefone"
                                    variant="standard"
                                    sx={{ mt: 2, mb: 2 }}
                                    value={scope.formData.fone || ''}
                                    onChange={(e) => this.emitFormDataChange({ fone: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    label="Cargo"
                                    variant="standard"
                                    sx={{ mt: 2, mb: 2 }}
                                    value={scope.formData.position || ''}
                                    onChange={(e) => this.emitFormDataChange({ position: e.target.value })}
                                />
                            </Box>

                            {PermSwitch('active', 'Ativo')}
                        </Card>

                        <Card elevation={0} className={classes.baseCard}>
                            <FormControl component="fieldset" variant="standard" sx={{ mr: 5 }}>
                                <FormLabel sx={{ mb: 1 }}>Permissões Gerais</FormLabel>
                                <FormGroup>
                                    {PermSwitch('compliant', 'Compliant')}
                                    {PermSwitch('the_map', 'The Map')}
                                    {PermSwitch('analise_desempenho', 'Análise de Desempenho')}
                                </FormGroup>
                            </FormControl>

                            <FormControl component="fieldset" variant="standard">
                                <FormLabel sx={{ mb: 1 }} component="legend">
                                    <label style={{ visibility: 'hidden' }}>Permissões</label>
                                </FormLabel>
                                <FormGroup>
                                    {PermSwitch('the_acting', 'The Acting')}
                                    {PermSwitch('the_acting_print', 'The Acting Print')}
                                    {PermSwitch('mercado_vet', 'Mercado Veterinário')}
                                </FormGroup>
                            </FormControl>

                            <FormControl component="fieldset" variant="standard">
                                <FormLabel sx={{ mb: 1 }} component="legend">
                                    <label style={{ visibility: 'hidden' }}>Permissões</label>
                                </FormLabel>
                                <FormGroup>
                                    {PermSwitch('the_connection', 'The Connection')}
                                    {PermSwitch('user_manager', 'User Manager')}
                                    {PermSwitch('motor_credito', 'Motor de Crédito')}
                                </FormGroup>
                            </FormControl>

                            <FormControl component="fieldset" variant="standard">
                                <FormLabel sx={{ mb: 1 }} component="legend">
                                    <label style={{ visibility: 'hidden' }}>Permissões</label>
                                </FormLabel>

                                <FormGroup>
                                    {PermSwitch('the_consumer_market', 'The Consumer Market')}
                                    {PermSwitch('the_consumer_market_ia', 'Consumer Market IA')}
                                </FormGroup>
                            </FormControl>
                        </Card>

                        {scope.adminGestaoEditable && (
                            <Card elevation={0} className={classes.baseCard}>
                                <FormControl component="fieldset" variant="standard">
                                    <FormLabel sx={{ mb: 1 }}>Gestão de acessos</FormLabel>
                                    {(scope.formData.user_manager && (
                                        <FormGroup>
                                            {PermSwitch('acesso_auditoria', 'Auditoria de Acesso')}
                                            {PermSwitch('acesso_faturamento', 'Faturamento')}
                                            {PermSwitch('admin_gestao', 'Gestor de acessos')}
                                        </FormGroup>
                                    )) || (
                                        <Box className={classes.alertBox}>
                                            <WarningAmberIcon color="warning" sx={{ fontSize: 28 }} />

                                            <Box>
                                                <Typography className={classes.alertTitle}>
                                                    User Manager desabilitado
                                                </Typography>
                                                <Typography variant="body2">
                                                    Auditoria de Acesso e Faturamento só valem para administradores
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </FormControl>
                            </Card>
                        )}

                        <Card elevation={0} className={classes.baseCard}>
                            <FormControl component="fieldset" variant="standard">
                                <FormLabel sx={{ mb: 1 }}>Motor de Crédito</FormLabel>
                                {(scope.formData.motor_credito && (
                                    <FormGroup>
                                        {PermSwitch('mc_bordero', 'Borderôs')}
                                        {PermSwitch('mc_analise', 'Análises')}
                                        {PermSwitch('mc_grupo', 'Grupos de Análise')}
                                        {PermSwitch('mc_monitoramento', 'Documentos Monitorados')}
                                        {PermSwitch('mc_pj', 'Pessoa Jurídica')}
                                        {PermSwitch('mc_pf', 'Pessoa Física')}
                                        {PermSwitch('mc_serasa', 'Serasa')}
                                        {PermSwitch('mc_serasa_history', 'Histórico Serasa')}
                                        {PermSwitch('mc_serpro', 'NF-e / SERPRO')}
                                        {PermSwitch('mc_admin', 'Administrador do Motor')}
                                    </FormGroup>
                                )) || (
                                    <Box className={classes.alertBox}>
                                        <WarningAmberIcon color="warning" sx={{ fontSize: 28 }} />

                                        <Box>
                                            <Typography className={classes.alertTitle}>
                                                Motor de Crédito desabilitado
                                            </Typography>
                                            <Typography variant="body2">
                                                Para liberar as telas do módulo, habilite o Motor de Crédito
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </FormControl>
                        </Card>

                        <Grid container spacing={2}>
                            <Grid size={6}>
                                <Card elevation={0} className={classes.baseCard}>
                                    <FormControl component="fieldset" variant="standard">
                                        <FormLabel sx={{ mb: 1 }}>Mercado IA</FormLabel>
                                        {(scope.formData.the_consumer_market && (
                                            <FormGroup>
                                                {PermSwitch('tcm_companies', 'Aba Empresas')}
                                                {PermSwitch('tcm_partners', 'Aba Sócios')}
                                                {PermSwitch('tcm_doctors', 'Aba Médicos')}
                                                {PermSwitch('tcm_relationships', 'Aba Vínculos')}
                                                {PermSwitch('tcm_enchente_rs_24', 'Filtro Enchente RS 2024')}
                                                {PermSwitch(
                                                    'tcm_filtro_cnpj_modo_clonagem',
                                                    'Filtro CNPJ Modo Clonagem'
                                                )}
                                            </FormGroup>
                                        )) || (
                                            <Box className={classes.alertBox}>
                                                <WarningAmberIcon color="warning" sx={{ fontSize: 28 }} />

                                                <Box>
                                                    <Typography className={classes.alertTitle}>
                                                        The Consumer Market desabilitado
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Para liberar as abas do módulo, habilite o The Consumer Market
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </FormControl>
                                </Card>
                            </Grid>

                            <Grid size={6}>
                                <Card elevation={0} className={classes.baseCard}>
                                    <FormControl component="fieldset" variant="standard">
                                        <FormLabel sx={{ mb: 1 }}>Relações Societárias</FormLabel>
                                        {(scope.formData.the_connection && (
                                            <FormGroup>
                                                {PermSwitch('tc_companies', 'Exportação de Empresas')}
                                                {PermSwitch('tc_partners', 'Exportação de Sócios')}
                                                {PermSwitch('tc_doctors', 'Exportação de Médicos')}
                                                {PermSwitch('tc_relationships', 'Exportação de Vínculos')}
                                            </FormGroup>
                                        )) || (
                                            <Box className={classes.alertBox}>
                                                <WarningAmberIcon color="warning" sx={{ fontSize: 28 }} />

                                                <Box>
                                                    <Typography className={classes.alertTitle}>
                                                        The Connection desabilitado
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Para liberar as exportações no módulo, habilite o The Connection
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </FormControl>
                                </Card>
                            </Grid>
                        </Grid>

                        <Card elevation={0} className={classes.baseCard}>
                            <FormControl component="fieldset" variant="standard">
                                <FormLabel sx={{ mb: 1 }}>Controle de Exportação</FormLabel>
                                <FormGroup>{PermSwitch('can_export_data', 'Pode exportar dados')}</FormGroup>
                            </FormControl>

                            {scope.formData.can_export_data && (
                                <>
                                    <Autocomplete
                                        multiple
                                        sx={{ mt: 2 }}
                                        options={scope.groups?.map((g) => g.id) || []}
                                        value={scope.formData.groups?.map((g) => g.id) || []}
                                        getOptionLabel={(id) =>
                                            scope.groups?.find((g) => g.id === id)?.name || String(id)
                                        }
                                        disableCloseOnSelect
                                        onChange={(_, newValue) => {
                                            const selectedGroups = scope.groups?.filter((g) => newValue.includes(g.id))
                                            this.emitFormDataChange({ groups: selectedGroups })
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Grupos" variant="standard" />
                                        )}
                                    />

                                    <FormControl fullWidth sx={{ mt: 3, mb: 2 }}>
                                        <InputLabel>Prioridade de limite de exportação</InputLabel>
                                        <Select
                                            label="Prioridade de limite de exportação"
                                            value={scope.formData.export_limit_reference || ''}
                                            onChange={(e) =>
                                                this.emitFormDataChange({
                                                    export_limit_reference: e.target.value as ExportLimitReference
                                                })
                                            }
                                        >
                                            <MenuItem value="">Selecione</MenuItem>
                                            <MenuItem value={ExportLimitReference.USER}>Usuário</MenuItem>
                                            <MenuItem value={ExportLimitReference.GROUP}>Grupo</MenuItem>
                                        </Select>
                                    </FormControl>

                                    {scope.formData.export_limit_reference === ExportLimitReference.USER && (
                                        <>
                                            {PermSwitch('unlimited_data_export', 'Exportação ilimitada')}
                                            {!scope.formData.unlimited_data_export && (
                                                <TextField
                                                    type="number"
                                                    label="Limite de dados"
                                                    variant="standard"
                                                    sx={{ mt: 2 }}
                                                    value={scope.formData.limit_export_quantity || ''}
                                                    onChange={(e) =>
                                                        this.emitFormDataChange({
                                                            limit_export_quantity: Number(e.target.value)
                                                        })
                                                    }
                                                />
                                            )}
                                        </>
                                    )}

                                    {scope.formData.export_limit_reference === ExportLimitReference.GROUP && (
                                        <FormControl fullWidth>
                                            <InputLabel>Grupo prioritário</InputLabel>
                                            <Select
                                                value={scope.formData.preferred_group_id || ''}
                                                onChange={(e) =>
                                                    this.emitFormDataChange({
                                                        preferred_group_id: Number(e.target.value)
                                                    })
                                                }
                                                label="Grupo prioritário"
                                            >
                                                {scope.formData.groups?.map((gp) => (
                                                    <MenuItem key={gp.id} value={gp.id}>
                                                        {gp.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </>
                            )}
                        </Card>

                        <Card elevation={0} className={classes.baseCard}>
                            {PermSwitch(
                                'sendWelcomeMail',
                                `${scope.userEditing?.id ? 'Reenviar' : 'Enviar'} e-mail de boas vindas`
                            )}
                        </Card>
                    </CardContent>

                    <CardActions sx={{ p: 2, gap: 2 }}>
                        <Button variant="contained" onClick={this.emitOnSave}>
                            Salvar
                        </Button>
                        <Button variant="outlined" onClick={this.emitOnClose}>
                            Fechar
                        </Button>
                    </CardActions>
                </Card>
            </div>
        )
    }
}

export const GrupoFormView = classToFComponent(UserManagerFormViewClass, React)
export default GrupoFormView
